import React, { useState } from 'react';
import Button from './shared/Button';
import ConfirmDialog from './shared/ConfirmDialog';

const packageJson = require('../../package.json');
const appVersion = `v${packageJson.version}`;

const HelpPanel = ({ 
  onClose, 
  userEmail, 
  isAuthReady, 
  userRole, 
  userTeam,
  userTeamName,
  assignedTemplate, 
  templatesLoading, 
  availableScripts, 
  currentScriptTemplate, 
  isBasic, 
  onLogout, 
  onResetApp, 
  onExportConversation, 
  showDebugInfo, 
  setShowDebugInfo,
  testCreateTemplate,
  testSetDefault,
  checkFirestorePermissions,
  setTemplateAsDefault,
  isAdmin,
  isTeamLead,
  canAccessScriptBuilder,
  canSwitchTemplates,
  canManageUsers,
  defaultTemplate,
  getEmailDocumentId,
  userId,
  loadUserDefaults,
  conversationHistory,
  setUserRole,
  onNavigateToHelpCenter
}) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  return (
    <div className="p-4">
      {/* User Authentication */}
      <div className="mb-3 p-3 bg-green-50 rounded-lg">
        <h3 className="font-semibold text-gray-700 mb-2">User Account</h3>
        <div className="text-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Signed in as:</span>
            <span className="font-medium text-gray-800">{userEmail}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isAuthReady ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-xs text-gray-500">
              {isAuthReady ? 'Connected' : 'Disconnected'}
            </span>
          </div>
                    
          {/* RBAC Info */}
          <div className="mt-3 pt-3 border-t border-green-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Role:</span>
              <span className={`font-medium px-2 py-1 rounded text-xs ${
                userRole === 'admin' ? 'bg-red-100 text-red-800' :
                  userRole === 'team_lead' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
              }`}>
                {userRole === 'admin' ? 'Super Admin' :
                  userRole === 'team_lead' ? 'Team Lead' :
                    userRole === 'basic' ? 'Agent' : 'Unknown'}
              </span>
            </div>
            {userTeam && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Team ID:</span>
                  <span className="font-medium text-gray-800">{userTeam}</span>
                </div>
                {userTeamName && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Team Name:</span>
                    <span className="font-medium text-gray-800">{userTeamName}</span>
                  </div>
                )}
              </>
            )}
          </div>
                    
          <Button
            variant="danger"
            size="sm"
            fullWidth
            onClick={onLogout}
            aria-label="Sign out of your account"
          >
            Sign Out
          </Button>
                    
          {/* App Version */}
          <div className="mt-3 pt-3 border-t border-green-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">App Version:</span>
              <span className="font-mono text-xs text-gray-500">{appVersion}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Help Center Link */}
      {onNavigateToHelpCenter && (
        <div className="mb-3">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={onNavigateToHelpCenter}
            aria-label="Open Help Center"
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          >
            Help Center
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-700 mb-2">Actions</h3>
        <div className="space-y-2">
          <Button
            variant="primary"
            size="sm"
            fullWidth
            onClick={() => setShowResetConfirm(true)}
            aria-label="Reset current call"
          >
            Reset App
          </Button>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={onExportConversation}
            aria-label="Export conversation history"
          >
            Export Conversation
          </Button>
        </div>
      </div>

      {/* Debug Information */}
      {showDebugInfo && (
        <div className="mb-3 p-3 bg-yellow-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">Debug Information</h3>
          <div className="text-xs space-y-1 text-gray-600">
            <div>User Role: {userRole}</div>
            <div>User Team: {userTeam}</div>
            <div>Can Access Script Builder: {canAccessScriptBuilder ? 'Yes' : 'No'}</div>
            <div>Can Switch Templates: {canSwitchTemplates ? 'Yes' : 'No'}</div>
            <div>Can Manage Users: {canManageUsers ? 'Yes' : 'No'}</div>
            <div>Is Admin: {isAdmin ? 'Yes' : 'No'}</div>
            <div>Is Team Lead: {isTeamLead ? 'Yes' : 'No'}</div>
          </div>
        </div>
      )}

      {/* Debug Actions */}
      {showDebugInfo && (
        <div className="mb-3 p-3 bg-orange-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">Debug Actions</h3>
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={testCreateTemplate}
              aria-label="Test creating a template"
            >
              Test Create Template
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={testSetDefault}
              aria-label="Test setting default template"
            >
              Test Set Default
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={checkFirestorePermissions}
              aria-label="Check Firestore permissions"
            >
              Check Firestore Permissions
            </Button>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => {
          onResetApp();
          setShowResetConfirm(false);
        }}
        title="Reset Current Call?"
        message="This will clear all conversation history and collected data for the current call."
        confirmLabel="Reset"
        confirmVariant="danger"
      />
    </div>
  );
};

export default HelpPanel;
